/**
 * Workspaces: the list, the create dialog, and the upgrade wall.
 *
 * The design draws workspace cards with a member count, a storage figure and an
 * unread-messages pill, plus a "locked" variant for accounts that cannot have
 * one. All three are here.
 *
 * The upgrade wall is the part worth being careful about. Gating is enforced by
 * the server — `POST /api/cloud/workspaces` returns `plan_required` for a free
 * account — and this screen reads the plan from `/me` so it can show the wall
 * *before* someone fills in a name and gets refused. Both halves are needed: a
 * client-only check would be trivially bypassed, and a server-only one would
 * make the feature look broken rather than paid.
 *
 * What the wall gates is **creating**, and only creating. It once stood in for
 * the whole page on an unpaid plan, which meant an invited member was shown a
 * pitch for the thing they had just been given — their workspaces were never
 * even fetched. The list always loads; the wall appears only when there is
 * nothing to show and nothing they could do about it.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { errorText } from '../api';
import { ds } from '../ds';
import { useApp } from '../state';
import { Link, useRouter } from '../router';
import { PageTitle, Panel } from '../components/Shell';
import { accountOrigin } from './Vault';
import { createWorkspace, listWorkspaces, type DecryptedWorkspace } from '../workspaces';
import { formatBytes, monoPlain } from '../theme';
import { SkeletonCards } from '../components/Skeleton';
import { useT } from '../i18n';

export function Workspaces() {
  const t = useT();
  const { Button, Icon, Input } = ds();
  const { me } = useApp();
  const { navigate } = useRouter();

  const [rows, setRows] = useState<DecryptedWorkspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  // Whether this account may *create* a workspace. Free is the normalised answer
  // for anything unpaid — the same rule the server applies.
  //
  // This is not the same question as whether they have any, and conflating the
  // two was a bug: someone on Demo who is *invited* to a workspace is a member
  // of it, holds its key, and has files and chat waiting for them. The page used
  // to skip the fetch entirely when `locked` and show the upgrade wall, so being
  // added to a team looked like nothing had happened. Membership is not
  // ownership; only the create path is gated.
  const canCreate = (me?.plan ?? 'free') !== 'free';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await listWorkspaces());
      setError(null);
    } catch (err) {
      setError(errorText(err, 'Could not load your workspaces.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);


  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const created = await createWorkspace(name);
      setCreating(false);
      setName('');
      navigate(`/workspaces/${created.id}`);
    } catch (err) {
      setError(errorText(err, 'Could not create that workspace.'));
    } finally {
      setBusy(false);
    }
  };

  // The wall stands in for the page only when there is genuinely nothing to
  // show *and* nothing they could do about it. A member with workspaces gets
  // the list; the sales pitch would be telling them to buy what they already have.
  if (!canCreate && !loading && rows.length === 0) return <UpgradeWall />;

  return (
    <div style={{ display: 'grid', gap: 24 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap'
        }}
      >
        <PageTitle
          title={t('workspaces.workspaces')}
          meta={`${rows.length} workspace${rows.length === 1 ? '' : 's'}`}
        />
        {canCreate ? (
          <Button variant="primary" size="md" onClick={() => setCreating((v) => !v)}>
            {t('workspaces.new-workspace')}
          </Button>
        ) : (
          // A member who cannot create one still needs to know why the button is
          // not there. An upgrade link says that; a disabled button reads as a
          // permissions fault with their account.
          <Button
            variant="ghost"
            size="md"
            onClick={() => {
              window.location.href = `${accountOrigin()}/pricing`;
            }}
          >
            {t('workspaces.upgrade-to-create-one')}
          </Button>
        )}
      </div>

      {error && (
        <Panel style={{ padding: '14px 20px', background: 'var(--danger-100)' }}>
          <span style={{ fontSize: 13.5, color: 'var(--danger-600)' }}>{error}</span>
        </Panel>
      )}

      {creating && (
        <Panel style={{ padding: '22px 26px' }}>
          <form onSubmit={submit} style={{ display: 'grid', gap: 14 }}>
            <Input
              label={t('workspaces.workspace-name')}
              value={name}
              onChange={(e) => setName((e.target as HTMLInputElement).value)}
              placeholder={t('workspaces.design-team')}
            />
            <div style={{ display: 'flex', gap: 10 }}>
              <Button variant="primary" size="md" type="submit" disabled={busy || !name.trim()}>
                {busy ? 'Creating…' : 'Create workspace'}
              </Button>
              <Button variant="ghost" size="md" onClick={() => setCreating(false)}>
                {t('workspaces.cancel')}
              </Button>
            </div>
          </form>
        </Panel>
      )}

      {loading ? (
        <SkeletonCards count={3} />
      ) : rows.length === 0 ? (
        <Panel style={{ padding: '40px 34px', display: 'grid', gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-strong)' }}>
            {t('workspaces.no-workspaces-yet')}
          </span>
          <span style={monoPlain}>{t('workspaces.create-one-to-share-files')}</span>
        </Panel>
      ) : (
        <div
          style={{
            display: 'grid',
            gap: 16,
            gridTemplateColumns: 'repeat(auto-fill, minmax(268px, 1fr))'
          }}
        >
          {rows.map((w) => (
            <Link key={w.id} to={`/workspaces/${w.id}`}>
              <Panel
                style={{
                  padding: '20px 22px',
                  display: 'grid',
                  gap: 12,
                  height: '100%',
                  cursor: 'pointer'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 10,
                      display: 'grid',
                      placeItems: 'center',
                      background: 'var(--accent-100)',
                      color: 'var(--accent-600)',
                      flex: '0 0 auto'
                    }}
                  >
                    <Icon name="users" size={18} />
                  </span>
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 15.5,
                      fontWeight: 700,
                      color: w.undecryptable ? 'var(--text-muted)' : 'var(--text-strong)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {w.name}
                  </span>
                  {w.unread > 0 && <UnreadPill count={w.unread} />}
                </div>

                <div style={monoPlain}>
                  {w.memberCount} member{w.memberCount === 1 ? '' : 's'}
                  {w.isOwner ? ' · you own this' : ` · you are ${w.role}`}
                </div>

                {/* Usage alone. A workspace draws on its owner's drive rather
                    than holding a pool of its own, so there is no second number
                    to put here — "of 200 GB" would be quoting a limit that does
                    not exist. The owner's remaining space is on the workspace
                    page itself, where it can be attributed to the right pool. */}
                <div style={monoPlain}>{formatBytes(w.storage.usedBytes)} stored</div>
              </Panel>
            </Link>
          ))}
        </div>
      )}

    </div>
  );
}

/** The design's "N new" pill. Counted server-side without decrypting a thing:
 *  messages carry a plaintext timestamp, and your read position is a number. */
export const UnreadPill = ({ count }: { count: number }) => (
  <span
    style={{
      flex: '0 0 auto',
      padding: '2px 8px',
      borderRadius: 999,
      background: 'var(--accent-600)',
      color: '#fff',
      fontSize: 11.5,
      fontWeight: 700,
      fontVariantNumeric: 'tabular-nums'
    }}
  >
    {count > 99 ? '99+' : count} new
  </span>
);

/**
 * What a free account sees.
 *
 * Deliberately not a disabled row in the sidebar: a greyed control reads as a
 * permissions problem with the account rather than as something to buy. Say
 * what it is, say what it costs, and link to the place that sells it.
 */
function UpgradeWall() {
  const t = useT();
  const { Button, Icon } = ds();

  return (
    <div style={{ display: 'grid', gap: 24 }}>
      <PageTitle title={t('workspaces.workspaces')} meta="Part of Max" />

      <Panel style={{ padding: '34px 30px', display: 'grid', gap: 18, maxWidth: 640 }}>
        <span
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            display: 'grid',
            placeItems: 'center',
            background: 'var(--accent-100)',
            color: 'var(--accent-600)'
          }}
        >
          <Icon name="users" size={22} />
        </span>

        <div style={{ display: 'grid', gap: 8 }}>
          <h2
            style={{
              fontFamily: "var(--font-display), 'Baloo 2', system-ui",
              fontWeight: 800,
              fontSize: 24,
              margin: 0,
              color: 'var(--text-strong)'
            }}
          >
            {t('workspaces.share-a-drive-with-your')}
          </h2>
          <span style={{ fontSize: 14.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
            {t('workspaces.a-shared-endtoend-encrypted-drive')}
          </span>
        </div>

        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 9 }}>
          {[
            'One pool, shared between your drive and your team’s',
            'Encrypted chat, readable only by the people in the workspace',
            'Add and remove members without re-uploading anything'
          ].map((line) => (
            <li
              key={line}
              style={{ display: 'flex', gap: 9, fontSize: 14, color: 'var(--text-body)' }}
            >
              <Icon name="check" size={16} />
              {line}
            </li>
          ))}
        </ul>

        <div>
          <Button
            variant="primary"
            size="md"
            onClick={() => {
              window.location.href = `${accountOrigin()}/pricing`;
            }}
          >
            {t('workspaces.see-plans')}
          </Button>
        </div>
      </Panel>
    </div>
  );
}
